/**
 * Environment variables — single source of truth for runtime config.
 *
 * All Vite env vars MUST be prefixed with VITE_. This module centralizes
 * access + type-safe defaults so the rest of the app never reads
 * `import.meta.env` directly.
 */

import { paptoqApiBases } from './paptoq-api';

const env = import.meta.env;

export const APP_ENV = {
  apiBaseUrl: paptoqApiBases()?.apiBaseUrl ?? ((env['VITE_API_BASE_URL'] as string) || '/api/v1'),
  devApiPort: (env['VITE_DEV_API_PORT'] as string) || '3000',
  defaultLocale: (env['VITE_DEFAULT_LOCALE'] as string) || 'tr',
  availableLocales: ((env['VITE_AVAILABLE_LOCALES'] as string) || 'tr,en')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean),
  sentryDsn: (env['VITE_SENTRY_DSN'] as string) || '',
  featureFlags: ((env['VITE_FEATURE_FLAGS'] as string) || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean),
  buildEnv: (env['VITE_BUILD_ENV'] as string) || 'local',
  buildVersion: (env['VITE_BUILD_VERSION'] as string) || 'dev',
  /**
   * Business namespace API base (`/api/biz/*`). Does NOT share
   * `apiBaseUrl` — Business routes are mounted under `/api` (not `/api/v1`)
   * and sit behind `business-tenant-context` preHandler (ADR-101 Layer 1).
   */
  bizApiBaseUrl: paptoqApiBases()?.bizApiBaseUrl ?? ((env['VITE_BIZ_API_BASE_URL'] as string) || '/api'),
  /**
   * Per-feature mock↔live switch. `mock` keeps deterministic fixtures for
   * Lovable/Storybook flows; `live` routes through bizClient to real
   * backend endpoints. Staged: flipped per feature as backend ships.
   */
  bizApiMode:
    (env['VITE_BIZ_API_MODE'] as 'mock' | 'live' | undefined) || 'mock',
  isDev: env.DEV === true,
  isProd: env.PROD === true,
} as const;

export type AppEnv = typeof APP_ENV;
